import {
  Box,
  Button,
  Grid,
  Radio,
  Stack,
  Typography,
} from "@mui/material";
import CardContent from "@mui/material/CardContent/CardContent";
import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  Edit,
  SimpleForm,
  TextInput,
  Create,
  Show,
  SimpleShowLayout,
  FunctionField,
  ListProps,
  EditProps,
  CreateProps,
  ShowProps,
  DateField,
  FilterLiveSearch,
  SavedQueriesList,
  useDataProvider,
  useNotify,
  useShowController,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { Cargo } from "../models/Cargo";
import { CargoCard } from "./components/cargo-card";
import {
  AsideStyledCard,
  CargoNameWithImageField,
  fixedRightAsideSx,
  fixedRightAsideListSx,
} from "./components/common";

const MAP_DRIVER_FETCH_FAILED_MESSAGE = "获取货源详情失败，无法打开地图司机";
const MAP_DRIVER_MISSING_COORDINATES_MESSAGE =
  "当前货源详情缺少始发地坐标，无法打开地图司机";

const getMapNavigationPayload = (cargo: Cargo | null | undefined) => {
  const coordinates = cargo?.from?.location?.coordinates;
  const longitude = cargo?.from?.longitude;
  const latitude = cargo?.from?.latitude;

  const lon =
    Array.isArray(coordinates) && coordinates.length >= 2
      ? coordinates[0]
      : longitude;
  const lat =
    Array.isArray(coordinates) && coordinates.length >= 2
      ? coordinates[1]
      : latitude;

  if (typeof lat !== "number" || typeof lon !== "number") {
    return null;
  }

  const address = [
    cargo?.from?.province,
    cargo?.from?.city,
    cargo?.from?.district,
    cargo?.from?.address,
  ]
    .filter(Boolean)
    .join("");

  return {
    lat,
    lon,
    address,
  };
};

const BooleanField: React.FC<{ source: string; label: string }> = ({
  source,
  label,
}) => (
  <FunctionField
    source={source}
    label={label}
    render={(record: Cargo) => (record[source as keyof Cargo] ? "是" : "否")}
  />
);

const Aside = ({
  selectedCargo,
  isNavigating,
  onGoToMapDrivers,
}: {
  selectedCargo: Cargo | null;
  isNavigating: boolean;
  onGoToMapDrivers: () => void;
}) => {
  return (
    <AsideStyledCard sx={fixedRightAsideSx}>
      <CardContent>
        <FilterLiveSearch source="name" label="货物名" alwaysOn />
        <SavedQueriesList />
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">地图司机</Typography>
          {selectedCargo ? (
            <Button
              variant="contained"
              fullWidth
              onClick={onGoToMapDrivers}
              disabled={isNavigating}
            >
              {isNavigating ? "正在获取坐标..." : "查看地图司机"}
            </Button>
          ) : null}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              已选货源
            </Typography>
            {selectedCargo ? (
              <CargoNameWithImageField cargo={selectedCargo} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                请先选择一条货源
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </AsideStyledCard>
  );
};

export const CargoList: React.FC<ListProps> = (props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const [selectedCargo, setSelectedCargo] = React.useState<Cargo | null>(null);
  const [isNavigating, setIsNavigating] = React.useState(false);
  const selectedCargoId = selectedCargo?.id;

  const handleSelectCargo = React.useCallback((cargo: Cargo) => {
    setSelectedCargo(cargo);
  }, []);

  const handleGoToMapDrivers = React.useCallback(async () => {
    if (!selectedCargo?.id || isNavigating) {
      return;
    }

    setIsNavigating(true);

    try {
      const { data } = await dataProvider.getOne("validCargos", {
        id: selectedCargo.id,
      });
      const detailCargo = data as Cargo;
      const mapNavigationPayload = getMapNavigationPayload(detailCargo);

      if (!mapNavigationPayload) {
        notify(MAP_DRIVER_MISSING_COORDINATES_MESSAGE, { type: "warning" });
        return;
      }

      const searchParams = new URLSearchParams({
        lat: String(mapNavigationPayload.lat),
        lon: String(mapNavigationPayload.lon),
        address: mapNavigationPayload.address,
        distance: "50",
      });

      navigate(`/map/map_drivers?${searchParams.toString()}`);
    } catch (error) {
      notify(MAP_DRIVER_FETCH_FAILED_MESSAGE, { type: "error" });
    } finally {
      setIsNavigating(false);
    }
  }, [dataProvider, isNavigating, navigate, notify, selectedCargo]);

  return (
    <List
      {...props}
      aside={
        <Aside
          selectedCargo={selectedCargo}
          isNavigating={isNavigating}
          onGoToMapDrivers={handleGoToMapDrivers}
        />
      }
      sx={fixedRightAsideListSx}
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <FunctionField
          label="选择"
          render={(record: Cargo) => (
            <Radio
              checked={record.id === selectedCargoId}
              onClick={(event) => {
                event.stopPropagation();
                handleSelectCargo(record);
              }}
            />
          )}
        />
        <TextField source="id" label="货号" />
        <FunctionField
          label="货物名称"
          sortBy="cargo.name"
          render={(record: Cargo) => <CargoNameWithImageField cargo={record} />}
        />
        <FunctionField
          label="发→收"
          render={(record: Cargo) => `${record.from.city}→ ${record.to.city}`}
        />
        <TextField source="unit_price" label="货物单价" />
        <DateField
          source="expect_departure_at"
          label="装车时间"
          showTime
          locales="zh-CN"
        />
        <FunctionField
          source="deposit"
          label="押金(元)"
          render={(record: Cargo) => `${(record.deposit / 100).toFixed(2)}`}
        />
        <BooleanField source="deposit_refund" label="是否退还押金" />
        <BooleanField source="continuous" label="是否是长期货源" />
        <BooleanField source="single_price" label="是否一口价" />
      </Datagrid>
    </List>
  );
};

export const CargoEdit: React.FC<EditProps> = (props) => (
  <Edit {...props}>
    <SimpleForm>
      <TextInput source="name" label="货物名" />
      <TextInput source="description" label="货物描述" />
      <TextInput source="unit_price" label="货物单价" />
    </SimpleForm>
  </Edit>
);

export const CargoCreate: React.FC<CreateProps> = (props) => (
  <Create {...props}>
    <SimpleForm>
      <TextInput source="name" label="货物名" />
      <TextInput source="description" label="货物描述" />
      <TextInput source="unit_price" label="货物单价" />
    </SimpleForm>
  </Create>
);

export const CargoShow: React.FC<ShowProps> = (props) => {
  const { record } = useShowController(props);
  return (
    <Show {...props}>
      <SimpleShowLayout>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <CargoCard cargo={record} />
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </Show>
  );
};
